import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getBrandLocaleEnvVarValue} from '../utils/site-config'
import {useBrand} from '../contexts/brand-context'

/**
 * Resolves the brand + locale specific customer service ("Contact us") URL from
 * the `CUSTOMER_SERVICE_URL` site preference.
 *
 * The brand is taken from `useBrand()`, which is resolved from the request host
 * during SSR and from the browser host on the client. This is why we don't let
 * the underlying helper resolve the brand from the host itself: on the server
 * `window` is undefined, so host-based detection would fall back to the default
 * brand and produce the wrong (Garage) URL on a Dynamite host.
 *
 * The resolved value carries brand/lang query params baked into the site
 * preference, but their casing is inconsistent (e.g. `lang=EN_ca`). We normalize
 * `lang` to `<language>_<COUNTRY>` (e.g. `en_CA`) and append the matching
 * `country` (e.g. `CA`), both derived from the locale, so the contact form is
 * scoped correctly with consistent casing.
 *
 * @returns {string|undefined} The resolved URL, or undefined when not configured.
 */
export const useCustomerServiceUrl = () => {
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const {brandId} = useBrand()

    const resolved = getBrandLocaleEnvVarValue(
        appConfig,
        'CUSTOMER_SERVICE_URL',
        site?.id,
        locale?.id,
        brandId
    )

    if (typeof resolved !== 'string' || !resolved) {
        return resolved
    }

    const [language, region] = String(locale?.id || '').split('-')
    if (!region) {
        return resolved
    }

    try {
        const url = new URL(resolved)
        url.searchParams.set('lang', `${language}_${region.toUpperCase()}`)
        url.searchParams.set('country', region.toUpperCase())
        return url.toString()
    } catch (_) {
        return resolved
    }
}
